import { JUNIOR_ELIGIBLE_SLOTS, isInstructionGroup } from "./tiers";
import { needsPlacing, hasNoFit } from "./placement";
import { getGroupSchedule, getOccupiedSlots } from "./time";
import {
  groupsScheduleOverlap,
  groupsTimeOverlap,
  hasAvailabilityConflict,
  getPreferredPartners,
} from "./validation";
import { coachShortName } from "./coachColors";
import { coachAvailableFor, hasAvailabilityConstraints } from "./coachAvailability";

// One place that answers "what's wrong with the current schedule?" so an
// admin can clear every problem before publishing, instead of chasing the
// same signals piecemeal across the grid, coaches, and roster tabs. Pure
// computation over existing data — every rule here already lives somewhere in
// the app; this just aggregates them into one ranked, clickable list.
//
// Each issue: { id, severity, category, title, detail?, groupIds?, playerId? }
// severity ∈ "error" | "warning" | "info". groupIds/playerId power click-to-jump.

const SEVERITY_RANK = { error: 0, warning: 1, info: 2 };

function whenLabel(group) {
  const { startTime } = getGroupSchedule(group);
  if (!group.day && !startTime) return "unscheduled";
  return [group.day, startTime].filter(Boolean).join(" ");
}

function coachLabel(coach) {
  return coach ? coachShortName(coach.name) || coach.name : "A coach";
}

export function getScheduleIssues(players, groups, coaches = []) {
  const issues = [];
  const coachMap = Object.fromEntries(coaches.map(c => [c.id, c]));

  // 1. Court double-booking — two groups on the same court/day at overlapping
  //    times. Only one can show per grid cell, so this is easy to miss.
  for (let i = 0; i < groups.length; i++) {
    for (let j = i + 1; j < groups.length; j++) {
      const a = groups[i];
      const b = groups[j];
      if (groupsScheduleOverlap(a, b)) {
        issues.push({
          id: `court-clash:${a.id}:${b.id}`,
          severity: "error",
          category: "Court double-booked",
          title: `${a.id} and ${b.id} share ${a.court}`,
          detail: `Both scheduled ${whenLabel(a)}. One court can't run two sessions at once.`,
          groupIds: [a.id, b.id],
        });
      }
    }
  }

  // 2. Coach double-booking — the same coach staffing two groups at once,
  //    regardless of court. One pro can't be in two places at the same time.
  const byCoach = {};
  groups.forEach(g => {
    if (g.coachId) (byCoach[g.coachId] ||= []).push(g);
  });
  Object.entries(byCoach).forEach(([coachId, coachGroups]) => {
    for (let i = 0; i < coachGroups.length; i++) {
      for (let j = i + 1; j < coachGroups.length; j++) {
        const a = coachGroups[i];
        const b = coachGroups[j];
        if (groupsTimeOverlap(a, b)) {
          issues.push({
            id: `coach-clash:${coachId}:${a.id}:${b.id}`,
            severity: "error",
            category: "Coach double-booked",
            title: `${coachLabel(coachMap[coachId])} is booked twice at once`,
            detail: `${a.id} and ${b.id} both run ${whenLabel(a)}.`,
            groupIds: [a.id, b.id],
          });
        }
      }
    }
  });

  // 2b. Coach scheduled outside the availability window they set.
  groups.forEach(g => {
    if (!g.coachId) return;
    const coach = coachMap[g.coachId];
    if (!coach || !hasAvailabilityConstraints(coach)) return;
    const { startTime, duration } = getGroupSchedule(g);
    if (!g.day || !startTime || !duration) return;
    if (!coachAvailableFor(coach, g.day, startTime, duration)) {
      issues.push({
        id: `coach-avail:${g.id}`,
        severity: "warning",
        category: "Coach unavailable",
        title: `${coachLabel(coach)} is scheduled outside their availability`,
        detail: `${g.id} runs ${whenLabel(g)}, outside the hours set for ${coach.name}.`,
        groupIds: [g.id],
      });
    }
  });

  // 3. Over-capacity instruction groups (more than 4 players on a court).
  groups.forEach(g => {
    if (isInstructionGroup(g) && (g.playerIds?.length || 0) > 4) {
      issues.push({
        id: `over-capacity:${g.id}`,
        severity: "error",
        category: "Over capacity",
        title: `${g.id} has ${g.playerIds.length} players`,
        detail: "Instruction groups are capped at 4 players per court.",
        groupIds: [g.id],
      });
    }
  });

  // 4. Coaches over their weekly hour cap.
  coaches.forEach(coach => {
    if (coach.maxHours == null) return;
    const hours = groups
      .filter(g => g.coachId === coach.id)
      .reduce((sum, g) => sum + (getGroupSchedule(g).duration || 0), 0);
    if (hours > coach.maxHours) {
      issues.push({
        id: `coach-cap:${coach.id}`,
        severity: "warning",
        category: "Coach over cap",
        title: `${coachLabel(coach)} is over the weekly cap`,
        detail: `${hours}h scheduled of a ${coach.maxHours}h/week cap.`,
        groupIds: groups.filter(g => g.coachId === coach.id).map(g => g.id),
      });
    }
  });

  // 5. Players scheduled into a slot they didn't list as available.
  players.forEach(p => {
    if (!p.groupId) return;
    const g = groups.find(gr => gr.id === p.groupId);
    if (!g) return;
    if (hasAvailabilityConflict(p, g)) {
      issues.push({
        id: `unavailable:${p.id}`,
        severity: "warning",
        category: "Player unavailable",
        title: `${p.name} isn't available for ${g.id}`,
        detail: `${g.id} runs ${whenLabel(g)}, which ${p.name} didn't list as available.`,
        groupIds: [g.id],
        playerId: p.id,
      });
    }
  });

  // 6. Junior groups scheduled outside junior-eligible hours.
  groups.forEach(g => {
    if (!isInstructionGroup(g)) return;
    if (!g.tier || !g.tier.startsWith("Junior")) return;
    const slots = getOccupiedSlots(g);
    if (slots.length > 0 && !slots.every(s => JUNIOR_ELIGIBLE_SLOTS.includes(s))) {
      issues.push({
        id: `junior-hours:${g.id}`,
        severity: "warning",
        category: "Outside junior hours",
        title: `${g.id} runs outside junior-eligible hours`,
        detail: `Scheduled ${whenLabel(g)}. Junior sessions should fall in the after-school window.`,
        groupIds: [g.id],
      });
    }
  });

  // 7. Preferred partners split across different groups.
  players.forEach(p => {
    const partners = getPreferredPartners(p, players);
    if (partners.length === 0) return;
    const apart = partners.filter(pt => !p.groupId || pt.groupId !== p.groupId);
    if (apart.length > 0) {
      issues.push({
        id: `partner:${p.id}`,
        severity: "info",
        category: "Partner preference",
        title: `${p.name} isn't grouped with ${apart.map(a => a.name).join(", ")}`,
        detail: p.groupId ? `${p.name} is in ${p.groupId}.` : `${p.name} is unassigned.`,
        playerId: p.id,
        groupIds: p.groupId ? [p.groupId] : [],
      });
    }
  });

  // 8. Scheduled groups (that need a coach) with none assigned. Open play
  //    never needs a coach, so it's excluded.
  groups.forEach(g => {
    const { startTime, duration } = getGroupSchedule(g);
    const scheduled = g.day && startTime && duration;
    if (scheduled && !g.coachId && g.kind !== "openplay") {
      issues.push({
        id: `no-coach:${g.id}`,
        severity: "info",
        category: "No coach",
        title: `${g.id} has no coach assigned`,
        detail: `Scheduled ${whenLabel(g)}.`,
        groupIds: [g.id],
      });
    }
  });

  // 9. Groups with players but no time/court yet (an unscheduled shell).
  groups.forEach(g => {
    const { startTime, duration } = getGroupSchedule(g);
    const scheduled = g.day && startTime && duration;
    if (!scheduled && (g.playerIds?.length || 0) > 0) {
      issues.push({
        id: `unscheduled:${g.id}`,
        severity: "info",
        category: "Unscheduled group",
        title: `${g.id} has ${g.playerIds.length} player(s) but no time slot`,
        detail: "Give it a day, court, and start time to place it on the grid.",
        groupIds: [g.id],
      });
    }
  });

  // 10. Unassigned players — rolled into a single info row so the list stays
  //     readable even with a big waitlist.
  // Members who have told the club they'll wait for next term are not an
  // outstanding job — see utils/placement.js.
  const unassigned = players.filter(needsPlacing);
  if (unassigned.length > 0) {
    issues.push({
      id: "unassigned-players",
      severity: "info",
      category: "Unassigned players",
      title: `${unassigned.length} player${unassigned.length !== 1 ? "s" : ""} not in any group`,
      detail: unassigned.slice(0, 8).map(p => p.name).join(", ") + (unassigned.length > 8 ? "…" : ""),
    });
  }

  // 11. Members who want a clinic but can't make any of the ones on offer.
  //     Not a placement job — there's nothing to place them into — but it is
  //     the demand signal for adding a slot, so it earns its own row rather
  //     than disappearing the way "waiting for next term" does.
  const noFit = players.filter(hasNoFit);
  if (noFit.length > 0) {
    issues.push({
      id: "no-schedule-fit",
      severity: "info",
      category: "Can't make any clinic",
      title: `${noFit.length} member${noFit.length !== 1 ? "s" : ""} can't make any clinic time`,
      detail: "They want a group but nothing on the schedule fits — worth a new slot. " +
        noFit.slice(0, 8).map(p => p.name).join(", ") + (noFit.length > 8 ? "…" : ""),
    });
  }

  // Stable, useful ordering: errors first, then warnings, then info; within a
  // severity, keep the category grouping the loops produced.
  return issues.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
}

// Count of actionable (non-info) issues — what the nav badge shows.
export function countActionableIssues(issues) {
  return issues.filter(i => i.severity !== "info").length;
}
